import { useState } from "react";
import { Search, Loader2, Package } from "lucide-react";
import { api, formatMoney } from "@/lib/api";

const STATUS_LABEL = {
  awaiting_payment: "Awaiting payment",
  processing: "Processing",
  packed: "Packed",
  shipped: "Shipped",
  delivered: "Delivered",
};

export default function TrackOrder() {
  const [orderId, setOrderId] = useState(localStorage.getItem("gobig_last_order") || "");
  const [email, setEmail] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const track = async (e) => {
    e.preventDefault();
    setError("");
    setOrder(null);
    setLoading(true);
    try {
      const { data } = await api.get("/orders/track", { params: { order_id: orderId, email } });
      setOrder(data);
    } catch (err) {
      setError(err.response?.data?.detail || "Order not found.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-5 sm:px-8 py-16">
      <h1 className="font-display text-4xl font-black tracking-tighter mb-3">Track your order</h1>
      <p className="text-muted-foreground mb-8">Enter your order ID and email to see status.</p>

      <form onSubmit={track} className="space-y-4 mb-10">
        <input value={orderId} onChange={(e) => setOrderId(e.target.value)} placeholder="Order ID" data-testid="track-order-id" className="w-full border-b border-ink/30 focus:border-brand outline-none bg-transparent py-3" required />
        <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email used at checkout" data-testid="track-email" className="w-full border-b border-ink/30 focus:border-brand outline-none bg-transparent py-3" required />
        <button type="submit" disabled={loading} className="btn-primary" data-testid="track-submit">
          {loading ? <Loader2 size={18} className="animate-spin" /> : <Search size={18} />} Track order
        </button>
      </form>

      {error && <p className="text-brand font-medium" data-testid="track-error">{error}</p>}

      {order && (
        <div className="border border-ink p-6" data-testid="track-result">
          <div className="flex items-center gap-3 mb-6">
            <Package className="text-brand" size={28} />
            <div>
              <p className="font-mono text-sm">{order.id}</p>
              <p className="font-display font-bold text-lg">{STATUS_LABEL[order.fulfillment_status] || order.fulfillment_status}</p>
            </div>
            <span className={`ml-auto text-xs font-bold uppercase px-3 py-1 ${order.status === "paid" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
              {order.status}
            </span>
          </div>
          <div className="space-y-2 text-sm">
            {order.items.map((it) => (
              <div key={`${it.product_id}-${it.size || ""}`} className="flex justify-between">
                <span>{it.quantity} × {it.name}{it.size ? ` · ${it.size}` : ""}</span>
                <span>{formatMoney(it.line_total, order.currency)}</span>
              </div>
            ))}
            <div className="flex justify-between font-bold font-display text-lg border-t border-ink/10 pt-2 mt-2">
              <span>Total</span><span>{formatMoney(order.total, order.currency)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
